// ============================================================
// Datei: frontend/src/pages/Settings.tsx
// Settings — mit User-Profil Management
// ============================================================
import React, { useState, useEffect } from 'react';
import { accountsApi, authApi, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const s = {
  page: { padding: '30px 40px', color: '#e2e8f0', minHeight: '100vh', maxWidth: 900 } as React.CSSProperties,
  title: { fontSize: 28, fontWeight: 700, marginBottom: 30, display: 'flex', alignItems: 'center', gap: 12 } as React.CSSProperties,
  section: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: 14, padding: 24, marginBottom: 20 } as React.CSSProperties,
  sectionTitle: { fontSize: 18, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, color: '#f1f5f9' } as React.CSSProperties,
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid #1e293b' } as React.CSSProperties,
  rowLast: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0' } as React.CSSProperties,
  label: { fontSize: 14, fontWeight: 600, color: '#e2e8f0' } as React.CSSProperties,
  sublabel: { fontSize: 12, color: '#64748b', marginTop: 2 } as React.CSSProperties,
  value: { fontSize: 14, color: '#94a3b8', fontFamily: 'monospace', background: '#1e293b', padding: '8px 14px', borderRadius: 8 } as React.CSSProperties,
  badge: { padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 } as React.CSSProperties,
  badgeOnline: { background: '#10b98133', color: '#34d399' } as React.CSSProperties,
  badgeOffline: { background: '#ef444433', color: '#ef4444' } as React.CSSProperties,
  btn: { background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  btnPrimary: { background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  btnSave: { background: '#22c55e', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  input: { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '8px 12px', color: '#e2e8f0', fontSize: 14, outline: 'none', width: 'min(250px, 100%)' } as React.CSSProperties,
  toggle: { width: 48, height: 26, borderRadius: 13, cursor: 'pointer', transition: 'all 0.2s', position: 'relative' as const, border: 'none' } as React.CSSProperties,
  toggleOn: { background: '#10b981' } as React.CSSProperties,
  toggleOff: { background: '#334155' } as React.CSSProperties,
  toggleKnob: { width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute' as const, top: 3, transition: 'all 0.2s' } as React.CSSProperties,
  techGrid: { display: 'flex', flexWrap: 'wrap' as const, gap: 12, marginTop: 8 } as React.CSSProperties,
  techBadge: { background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center' } as React.CSSProperties,
  techLabel: { fontSize: 12, color: '#64748b' } as React.CSSProperties,
  techValue: { fontSize: 14, fontWeight: 700, color: '#e2e8f0' } as React.CSSProperties,
  footer: { textAlign: 'center' as const, color: '#475569', fontSize: 13, marginTop: 40, paddingBottom: 30 } as React.CSSProperties,
  success: { background: '#22c55e20', border: '1px solid #22c55e66', color: '#86efac', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13 } as React.CSSProperties,
  error: { background: '#ef444420', border: '1px solid #ef444466', color: '#fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13 } as React.CSSProperties,
};

const Settings: React.FC = () => {
  const { user, updateUser, logout } = useAuth();
  const toast = useToast();

  const [backendStatus, setBackendStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [backendVersion, setBackendVersion] = useState('');
  const [darkMode, setDarkMode] = useState(true);
  const [dbUrl] = useState('postgres://localhost:5432/trading_engine');

  // Profile edit state
  const [editUsername, setEditUsername] = useState(user?.username || '');
  const [editEmail, setEditEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  // AI Configuration
  const [aiProvider, setAiProvider] = useState(localStorage.getItem('ai_provider') || 'openai');
  const [aiApiKey, setAiApiKey] = useState(localStorage.getItem('ai_api_key') || '');
  const [aiModel, setAiModel] = useState(localStorage.getItem('ai_model') || '');
  const saveAiSettings = () => {
    localStorage.setItem('ai_provider', aiProvider);
    localStorage.setItem('ai_api_key', aiApiKey);
    localStorage.setItem('ai_model', aiModel);
    toast.success('AI Settings saved!', 'Your API key is stored locally in your browser.');
  };

  useEffect(() => {
    checkHealth();
  }, []);

  useEffect(() => {
    if (user) {
      setEditUsername(user.username);
      setEditEmail(user.email);
    }
  }, [user]);

  const checkHealth = async () => {
    setBackendStatus('checking');
    try {
      const res = await axios.get(`${API_BASE.replace('/api', '')}/health`);
      setBackendStatus('online');
      setBackendVersion(res.data?.version || '2.2.0');
    } catch {
      try {
        await accountsApi.list();
        setBackendStatus('online');
        setBackendVersion('2.2.0');
      } catch {
        setBackendStatus('offline');
      }
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const data: any = {};
      if (editUsername !== user?.username) data.username = editUsername;
      if (editEmail !== user?.email) data.email = editEmail;
      if (currentPassword && newPassword) {
        data.current_password = currentPassword;
        data.new_password = newPassword;
      }

      if (Object.keys(data).length === 0) {
        toast.warning('No changes', 'You have not changed anything.');
        setSaving(false);
        return;
      }

      const res = await authApi.updateProfile(data);
      updateUser(res.data);
      toast.success('Profile updated!', 'Your changes have been saved.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast.error('Error saving', getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const exportData = async () => {
    try {
      const accounts = await accountsApi.list();
      const allTrades: any[] = [];
      for (const acc of accounts.data) {
        try {
          const res = await axios.get(`${API_BASE}/trades?account_id=${acc.id}`);
          allTrades.push(...res.data);
        } catch {}
      }
      const journalRes = await axios.get(`${API_BASE}/journal`);

      const exportObj = {
        exported_at: new Date().toISOString(),
        version: '2.2.0',
        user: user,
        accounts: accounts.data,
        trades: allTrades,
        journal: journalRes.data,
      };

      const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trading-engine-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export successful!', 'File has been downloaded.');
    } catch (err) {
      toast.error('Export failed', String(err));
    }
  };

  return (
    <div style={s.page}>
      <h1 style={s.title}>⚙️ Settings</h1>

      {/* 👤 User Profil */}
      {user && (
        <div style={{ ...s.section, border: '1px solid #8b5cf644' }}>
          <div style={s.sectionTitle}>👤 Your Profile</div>



          <div style={s.row}>
            <div>
              <div style={s.label}>Username</div>
              <div style={s.sublabel}>Your display name</div>
            </div>
            <input
              style={s.input}
              value={editUsername}
              onChange={(e) => setEditUsername(e.target.value)}
            />
          </div>

          <div style={s.row}>
            <div>
              <div style={s.label}>E-Mail</div>
              <div style={s.sublabel}>Your login email</div>
            </div>
            <input
              style={s.input}
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
            />
          </div>

          <div style={s.row}>
            <div>
              <div style={s.label}>Current Password</div>
              <div style={s.sublabel}>Only needed for password change</div>
            </div>
            <input
              style={s.input}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          <div style={s.row}>
            <div>
              <div style={s.label}>New Password</div>
              <div style={s.sublabel}>Min. 6 characters</div>
            </div>
            <input
              style={s.input}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          <div style={{ ...s.rowLast, gap: 10 }}>
            <div>
              <div style={s.label}>Member since</div>
              <div style={{ ...s.sublabel, color: '#8b5cf6' }}>
                {new Date(user.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={s.btnSave} onClick={saveProfile} disabled={saving}>
                {saving ? '⏳...' : '💾 Save'}
              </button>
              <button
                style={{ ...s.btn, color: '#ef4444', borderColor: '#ef444466' }}
                onClick={() => { if (window.confirm('Really log out?')) logout(); }}
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔌 Backend */}
      <div style={s.section}>
        <div style={s.sectionTitle}>🔌 Backend Connection</div>
        <div style={s.row}>
          <div>
            <div style={s.label}>API URL</div>
            <div style={s.sublabel}>URL of the Rust backend server</div>
          </div>
          <span style={s.value}>{API_BASE}</span>
        </div>
        <div style={s.row}>
          <div>
            <div style={s.label}>Status</div>
            <div style={s.sublabel}>Current connection to backend</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{
              ...s.badge,
              ...(backendStatus === 'online' ? s.badgeOnline : backendStatus === 'offline' ? s.badgeOffline : { background: '#eab30833', color: '#fbbf24' })
            }}>
              {backendStatus === 'online' ? '✓ Online' : backendStatus === 'offline' ? '✗ Offline' : '⏳ Checking...'}
            </span>
            <button style={s.btn} onClick={checkHealth}>Check again</button>
          </div>
        </div>
        <div style={s.rowLast}>
          <div>
            <div style={s.label}>Version</div>
            <div style={s.sublabel}>Backend version</div>
          </div>
          <span style={s.value}>v{backendVersion || '?.?.?'}</span>
        </div>
      </div>

      {/* 🎨 Darstellung */}
      <div style={s.section}>
        <div style={s.sectionTitle}>🎨 Appearance</div>
        <div style={s.rowLast}>
          <div>
            <div style={s.label}>Dark Mode</div>
            <div style={s.sublabel}>Dark color scheme for the application</div>
          </div>
          <button style={{ ...s.toggle, ...(darkMode ? s.toggleOn : s.toggleOff) }} onClick={() => setDarkMode(!darkMode)}>
            <div style={{ ...s.toggleKnob, left: darkMode ? 25 : 3 }} />
          </button>
        </div>
      </div>

      {/* 🗄️ Database */}
      <div style={s.section}>
        <div style={s.sectionTitle}>🗄️ Database</div>
        <div style={s.rowLast}>
          <div>
            <div style={s.label}>PostgreSQL</div>
            <div style={s.sublabel}>Database for accounts, trades and journal</div>
          </div>
          <span style={s.value}>{dbUrl}</span>
        </div>
      </div>

      {/* 💾 Daten */}
      <div style={s.section}>
        <div style={s.sectionTitle}>💾 Data</div>
        <div style={s.row}>
          <div>
            <div style={s.label}>Export</div>
            <div style={s.sublabel}>Export all data as JSON</div>
          </div>
          <button style={s.btnPrimary} onClick={exportData}>📥 Export data</button>
        </div>
        <div style={s.rowLast}>
          <div>
            <div style={s.label}>Reset Database</div>
            <div style={s.sublabel}>⚠️ Deletes ALL data permanently</div>
          </div>
          <button style={{ ...s.btn, color: '#ef4444', borderColor: '#ef444466' }}
            onClick={() => alert('Use pgAdmin for manual resets.')}>
            🗑️ Reset
          </button>
        </div>
      </div>

      {/* 📚 Tech Stack */}
      <div style={s.section}>
        <div style={s.sectionTitle}>📚 Tech Stack</div>
        <div style={s.techGrid}>
          {[
            ['Backend', 'Rust + Axum'],
            ['Frontend', 'React + TS'],
            ['Database', 'PostgreSQL'],
            ['ORM', 'SQLx 0.8'],
            ['Auth', 'JWT + bcrypt'],
            ['Charts', 'Recharts'],
            ['Market Data', 'Yahoo Finance'],
            ['HTTP', 'Axios + Reqwest'],
          ].map(([label, value]) => (
            <div key={label} style={s.techBadge}>
              <span style={s.techLabel}>{label}</span>
              <span style={s.techValue}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      
      {/* AI Trading Bot */}
      <div style={s.section}>
        <div style={s.sectionTitle}>🤖 AI Trading Bot</div>
        <div style={s.row}>
          <div><div style={s.label}>Provider</div><div style={s.sublabel}>Choose your AI provider</div></div>
          <select style={{...s.input, width: 'min(200px, 100%)'}} value={aiProvider} onChange={e => setAiProvider(e.target.value)}>
            <option value="openai">OpenAI (GPT)</option>
            <option value="anthropic">Anthropic (Claude)</option>
          </select>
        </div>
        <div style={s.row}>
          <div><div style={s.label}>API Key</div><div style={s.sublabel}>Stored locally in your browser only</div></div>
          <input style={{...s.input, width: 'min(300px, 100%)'}} type="password" placeholder={aiProvider === 'openai' ? 'sk-...' : 'sk-ant-...'} value={aiApiKey} onChange={e => setAiApiKey(e.target.value)} />
        </div>
        <div style={s.row}>
          <div><div style={s.label}>Model (optional)</div><div style={s.sublabel}>{aiProvider === 'openai' ? 'Default: gpt-4o-mini' : 'Default: claude-haiku-4-5-20251001'}</div></div>
          <input style={{...s.input, width: 'min(250px, 100%)'}} placeholder={aiProvider === 'openai' ? 'gpt-4o-mini' : 'claude-haiku-4-5-20251001'} value={aiModel} onChange={e => setAiModel(e.target.value)} />
        </div>
        <div style={{...s.rowLast, justifyContent: 'flex-end'}}>
          <button style={s.btnSave} onClick={saveAiSettings}>💾 Save AI Settings</button>
        </div>
      </div>

      <div style={s.footer}>
        Trading Engine v2.2.0 — Auth Edition<br />
        Built with Rust 🦀 + React 💎
      </div>
    </div>
  );
};

export default Settings;